/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import JsonQuery from 'json-query';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import { Headings } from '../Header/Header';
import HeaderWrapper from '../Header/HeaderWrapper';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import downloadBoxStyles, { downloadThemeBoxStyles } from './DownloadBox.styles';

async function makeRequest(url: string, method: string, body: BodyInit | null) {
    if (!url) return;

    const headers: HeadersInit = {};
    const options: RequestInit = {
        method,
        headers,
    };

    if (body) {
        headers['Content-Type'] = 'application/json';
        options.body = body;
    }

    return fetch(url, options).then((response: Response) => {
        if (method === 'GET') {
            if (!response.ok) {
                return response.json().then((o: any) => {
                    throw new Error(o.message);
                });
            } else {
                return response.json();
            }
        } else {
            if (!response.ok) {
                return response.text().then((o: any) => {
                    throw new Error(o.message);
                });
            } else {
                return response.text();
            }
        }
    });
}

interface HeaderOption {
    headerTitle: Headings[];
    headerIcon: boolean;
    headerIconType: 'Font-awesome' | 'Icon-URL';
    headerIconName: string;
    headerIconURL: string;
    headerIconPosition: 'Right' | 'Left';
}

interface DownloadAllButtonOption {
    buttonName: string;
    showIcon: boolean;
    iconType: string;
    iconName: string;
    iconURL: string;
    iconPosition: 'Right' | 'Left';
}

interface DownloadOption {
    showDocumentThumbnail: boolean;
    documentThumbnailPosition: 'Right' | 'Left';
    documentThumbnailURLDatakey: string;
    defaultThumbnailURL: string;
    documentNameDatakey: string;
    isHideDocumentCheckboxes: boolean;
    isHideShareIcons: boolean;
    isHideDownloadIcons: boolean;
    downloadLinkDatakey: string;
    downloadIconType: 'Font-awesome' | 'Icon-URL';
    downloadIconName: string;
    downloadIconURL: string;
    downloadIconPosition: 'Right' | 'Left';
}

interface ShareOption {
    shareIconType: 'Font-awesome' | 'Icon-URL';
    shareIconName: string;
    shareIconURL: string;
    shareLinkDatakey: string;
    shareLinkTitle: string;
}

export interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    isMultipleDownload: boolean;
    showHeader: boolean;
    dataKey: string;
    headerOption: HeaderOption;
    downloadAllButtonOption: DownloadAllButtonOption;
    downloadPackageMessage: string;
    downloadPackageError: string;
    downloadPackageLinkText: string;
    downloadPackageText: string;
    packageName: string;
    downloadOptions: DownloadOption;
    shareOptions: ShareOption;
    sortDocumentsBy: string;
    typographyClass?: string;
    functionToTransform?: string;
    isTransformData?: boolean;
}

const DownloadBox = (props: commonTypes.WidgetInstanceProps): JSX.Element => {
    const { isClient } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, headerOption, downloadAllButtonOption, downloadOptions, ...rest } =
        widgetProperties || props;

    const i18n = commonHooks.useLocalization();

    const {
        documentNameDatakey,
        downloadLinkDatakey,
        documentThumbnailURLDatakey,
        defaultThumbnailURL,
        isHideDocumentCheckboxes,
        isHideShareIcons,
        isHideDownloadIcons,
    } = downloadOptions || {};
    const { shareLinkDatakey, shareLinkTitle, shareIconName, shareIconURL, shareIconType } =
        rest?.shareOptions || {};
    const { data } = props;
    const theme = data?.themeSelected || props?.theme;
    let isRender = {
        transformedData: data,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            rest.isTransformData &&
            commonUtils.transformDataWithGlobals(
                transformFunctionName,
                rest.functionToTransform,
                data
            );

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    const _documentsData: any = rest.dataKey
        ? JsonQuery(`${rest.dataKey}`, { data: isRender.transformedData }).value
        : null;
    const sortOrder: any = rest.sortDocumentsBy?.split(',');
    const documentsData: any = Array.isArray(sortOrder)
        ? _.sortBy(_documentsData, (doc) =>
              sortOrder.indexOf(doc?.code) !== -1
                  ? sortOrder.indexOf(doc?.code)
                  : _documentsData.length
          )
        : _documentsData;
    const [selectedLinks, setSelectedLinks] = useState<boolean[]>([]);
    const [packageURL, setPackageURL] = useState<string>('');
    const [requestState, setRequestState] = useState<string>('');

    const baseURL = `/api/package`;
    const apiBaseURL =
        isClient && window?.location?.hostname === 'localhost'
            ? `http://localhost:3001/api/package`
            : baseURL;

    useEffect(() => {
        if (documentsData?.length > 0) {
            setSelectedLinks(new Array(documentsData?.length).fill(false));
        }
    }, [documentsData?.length]);

    // Render Header Icon
    const renderHeaderIcon = (opt: HeaderOption, position: string) => (
        <span className={`header-icon header-icon-${position}`}>
            {opt.headerIconType === 'Font-awesome' ? (
                <i className={opt.headerIconName} />
            ) : (
                <img className="header-icon" src={opt.headerIconURL} alt={'header-icon'} />
            )}
        </span>
    );

    // Render Download All Button
    const renderDownloadAllButtonIcon = (opt: DownloadAllButtonOption, position: string) => (
        <span className={`download-all-icon download-all-icon-${position}`}>
            {opt.iconType === 'Font-awesome' ? (
                <i className={opt.iconName} />
            ) : (
                <img className="download-all-icon" src={opt.iconURL} alt={'download-all-icon'} />
            )}
        </span>
    );

    // Render Document Thumbnail
    const renderDocumentThumbnail = (opt: any, position: string) => {
        const thumbnailURL = i18n.format(documentThumbnailURLDatakey, opt);
        const imgSrc = thumbnailURL || defaultThumbnailURL;
        return (
            <span className={`document-thumbnail document-thumbnail-${position}`}>
                <img className="document-thumbnail" src={imgSrc} alt={'document-thumbnail'} />
            </span>
        );
    };

    // Render Download Icon
    const renderDownloadIcon = (opt: any, position: string) => (
        <span className={`download-icon download-icon-${position}`}>
            <a href={i18n.format(downloadLinkDatakey, opt)} target="_blank" rel="noreferrer">
                {downloadOptions.downloadIconType === 'Font-awesome' ? (
                    <i className={downloadOptions.downloadIconName} />
                ) : (
                    <img
                        className="download-icon"
                        src={downloadOptions.downloadIconURL}
                        alt={'download-icon'}
                    />
                )}
            </a>
        </span>
    );

    const onClickLink = (index: number) => {
        const newSelectedLinks = selectedLinks?.map((l: any, i: number) => (i === index ? !l : l));
        setSelectedLinks(newSelectedLinks);
    };

    const onDownloadAllClick = async () => {
        // get selected links indexes
        const selectedIndexes = selectedLinks
            ?.map((l, i: number) => !!l && i)
            ?.filter((s) => typeof s === 'number');

        const links: string[] = [];
        documentsData?.forEach((opt: any, i: number) => {
            const isSelected = selectedIndexes.find((s) => s === i);

            if (isSelected === i) links.push(i18n.format(downloadLinkDatakey, opt));
        });

        if (links?.length === 0) {
            return;
        }

        const body = { pdfUrls: links };

        setRequestState('PENDING');

        makeRequest(`${baseURL}/publish`, 'POST', JSON.stringify(body))
            .then((id) => {
                let hasDownloadedPackage = false;
                const statusInterval = setInterval(() => {
                    makeRequest(`${baseURL}/status?publicationId=${id}`, 'GET', null).then(
                        (data) => {
                            if (data?.status.toLowerCase() === 'completed') {
                                clearInterval(statusInterval);
                                hasDownloadedPackage = true;
                                setPackageURL(
                                    `${apiBaseURL}/download?id=${id}&filename=${rest?.packageName}`
                                );
                                setRequestState('SUCCESS');
                            } else if (data?.status.toLowerCase() === 'failed') {
                                clearInterval(statusInterval);
                                setRequestState('FAILED');
                            }
                        }
                    );
                }, 5000);

                // Set a timeout of 10 minutes before returning an error
                setTimeout(() => {
                    clearInterval(statusInterval);

                    if (!hasDownloadedPackage) {
                        setRequestState('FAILED');
                    }
                }, 600000);
            })
            .catch((error: any) => {
                setRequestState('FAILED');
                return error;
            });
    };

    return (
        <div
            className={`download-list ${
                widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
            }`}
            css={css`
                ${!theme ? downloadBoxStyles : downloadThemeBoxStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
        >
            {rest.showHeader && (
                <div className="header">
                    {headerOption?.headerIcon &&
                        headerOption?.headerIconPosition === 'Left' &&
                        renderHeaderIcon(headerOption, 'left')}
                    <HeaderWrapper headings={headerOption?.headerTitle} data={data} />
                    {headerOption?.headerIcon &&
                        headerOption?.headerIconPosition === 'Right' &&
                        renderHeaderIcon(headerOption, 'right')}
                    {rest.isMultipleDownload && (
                        <div className="button-container">
                            <button
                                className="download-all-button"
                                data-testid="download-all-button"
                                onClick={() => onDownloadAllClick()}
                            >
                                {downloadAllButtonOption?.showIcon &&
                                    downloadAllButtonOption?.iconPosition === 'Left' &&
                                    renderDownloadAllButtonIcon(downloadAllButtonOption, 'left')}
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.format(
                                            downloadAllButtonOption.buttonName,
                                            data
                                        ),
                                    }}
                                />
                                {downloadAllButtonOption?.showIcon &&
                                    downloadAllButtonOption?.iconPosition === 'Right' &&
                                    renderDownloadAllButtonIcon(downloadAllButtonOption, 'right')}
                            </button>
                            <div className="package-messages-container">
                                {requestState === 'PENDING' && (
                                    <div className={'package-info'}>
                                        <div className="loader" />
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(
                                                    rest.downloadPackageMessage,
                                                    data
                                                ),
                                            }}
                                        />
                                    </div>
                                )}

                                {requestState === 'FAILED' && (
                                    <div className="package-error">
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(
                                                    rest.downloadPackageError,
                                                    data
                                                ),
                                            }}
                                        />
                                    </div>
                                )}

                                {requestState === 'SUCCESS' && packageURL && (
                                    <div className={'package-success'}>
                                        <a
                                            href={packageURL}
                                            target="_blank"
                                            rel="noreferrer"
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(
                                                    rest.downloadPackageLinkText,
                                                    data
                                                ),
                                            }}
                                        />
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(rest.downloadPackageText, data),
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {!_.isEmpty(documentsData) &&
                documentsData?.map((opt: any, index: number) => (
                    <div key={index} className="download-item">
                        {downloadOptions?.showDocumentThumbnail &&
                            !isHideDownloadIcons &&
                            downloadOptions?.documentThumbnailPosition === 'Left' &&
                            renderDocumentThumbnail(opt, 'left')}
                        {downloadOptions?.downloadIconPosition === 'Left' &&
                            !isHideDownloadIcons &&
                            renderDownloadIcon(opt, 'left')}
                        <div className="document">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: i18n.format(documentNameDatakey, opt),
                                }}
                            />
                            {!isHideShareIcons && (
                                <span className={`share-icon`}>
                                    <a
                                        href={i18n.format(shareLinkDatakey, { opt, ...data })}
                                        title={i18n.format(shareLinkTitle, { opt, ...data })}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {shareIconType === 'Font-awesome' ? (
                                            <i className={shareIconName} />
                                        ) : (
                                            <img
                                                className="share-icon"
                                                src={shareIconURL}
                                                alt={'share-icon'}
                                            />
                                        )}
                                    </a>
                                </span>
                            )}
                            {!isHideDocumentCheckboxes && (
                                <label className="input-container">
                                    <input
                                        type="checkbox"
                                        id="document"
                                        name="document"
                                        value={i18n.format(documentNameDatakey, opt)}
                                        checked={selectedLinks[index]}
                                        onChange={() => onClickLink(index)}
                                    />
                                    <span className="checkbox" />
                                </label>
                            )}
                        </div>
                        {downloadOptions?.showDocumentThumbnail &&
                            !isHideDownloadIcons &&
                            downloadOptions?.documentThumbnailPosition === 'Right' &&
                            renderDocumentThumbnail(opt, 'right')}
                        {downloadOptions?.downloadIconPosition === 'Right' &&
                            !isHideDownloadIcons &&
                            renderDownloadIcon(opt, 'right')}
                    </div>
                ))}
        </div>
    );
};

export default DownloadBox;
